"use client";

import { useEffect, type RefObject } from "react";

export type ScrollProgressMode = "sticky" | "through";

/**
 * Pure progress math, shared by the hook and its tests.
 * - sticky: 0 when the track's top meets the viewport top, 1 when its bottom meets the viewport bottom.
 * - through: 0 when the element's top enters from below, 1 when its bottom leaves at the top.
 */
export const getScrollProgress = (
  mode: ScrollProgressMode,
  top: number,
  height: number,
  viewportHeight: number,
) => {
  const clamp = (value: number) => Math.min(1, Math.max(0, value));
  if (mode === "sticky") {
    const distance = height - viewportHeight;
    return distance > 0 ? clamp(-top / distance) : 0;
  }
  return clamp((viewportHeight - top) / (viewportHeight + height));
};

/**
 * Calls `onProgress` with 0 to 1 as the element scrolls, at most once per frame.
 * No React state, so scrubbing never re-renders.
 */
export const useScrollProgress = (
  ref: RefObject<HTMLElement | null>,
  onProgress: (progress: number) => void,
  mode: ScrollProgressMode = "sticky",
  enabled = true,
) => {
  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    let frame = 0;
    let last = -1;
    const measure = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const progress = getScrollProgress(
        mode,
        rect.top,
        rect.height,
        window.innerHeight,
      );
      if (progress !== last) {
        last = progress;
        onProgress(progress);
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref, onProgress, mode, enabled]);
};
